/**
 * Upload API - Firebase Storage Integration
 *
 * Handles file uploads to Firebase Storage for various contexts:
 * - Listing images (multiple per section)
 * - Profile avatars (single image)
 * - Verification documents (PDF/images)
 *
 * The storage instance is pre-configured in firebase.ts to automatically use:
 * - Local environment: gs://apaato-local.firebasestorage.app (emulator)
 * - Other environments: Production/staging bucket from .env
 */

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "@/shared/lib/firebase";

export interface UploadedImage {
  url: string;
  thumbnail_url: string;
  caption?: string;
}

export interface UploadedFile {
  url: string;
  filename: string;
  size: number;
  mimeType: string;
}

/**
 * Upload a listing image to Firebase Storage
 *
 * @param file - The image file to upload
 * @param listingId - The listing ID (used in storage path)
 * @param section - The section name (e.g., "exterior", "living_room")
 * @returns Object with url and thumbnail_url
 */
export async function uploadListingImage(
  file: File,
  listingId: string,
  section: string,
): Promise<UploadedImage> {
  try {
    // Generate unique filename using crypto.randomUUID()
    const fileExtension = file.name.split(".").pop() || "jpg";
    const uniqueFilename = `${crypto.randomUUID()}.${fileExtension}`;
    const storagePath = `listings/${listingId}/${section}/${uniqueFilename}`;

    // Create storage reference using pre-configured storage instance
    const storageRef = ref(storage, storagePath);

    // Upload file with metadata
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    // Get download URL
    const url = await getDownloadURL(snapshot.ref);

    // Log upload success
    console.log("✅ Uploaded:", snapshot.ref.fullPath);
    console.log("🔗 Download URL:", url);

    // For now, use full-size URL as thumbnail
    // TODO: Generate proper thumbnails (separate task)
    const thumbnail_url = url;

    return {
      url,
      thumbnail_url,
    };
  } catch (error) {
    console.error("Failed to upload image:", error);
    throw new Error(
      `Failed to upload image: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

/**
 * Upload multiple listing images
 *
 * @param files - Array of image files
 * @param listingId - The listing ID
 * @param section - The section name
 * @returns Array of uploaded image objects
 */
export async function uploadListingImages(
  files: File[],
  listingId: string,
  section: string,
): Promise<UploadedImage[]> {
  const uploadPromises = files.map((file) =>
    uploadListingImage(file, listingId, section),
  );

  return Promise.all(uploadPromises);
}

/**
 * Generic file upload to Firebase Storage
 *
 * @param file - The file to upload
 * @param storagePath - The full storage path (e.g., "profiles/userId/avatar.jpg")
 * @returns UploadedFile object with metadata
 */
export async function uploadFile(
  file: File,
  storagePath: string,
): Promise<UploadedFile> {
  try {
    const storageRef = ref(storage, storagePath);

    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    const url = await getDownloadURL(snapshot.ref);

    console.log("✅ Uploaded:", snapshot.ref.fullPath);
    console.log("🔗 Download URL:", url);

    return {
      url,
      filename: file.name,
      size: file.size,
      mimeType: file.type,
    };
  } catch (error) {
    console.error("Failed to upload file:", error);
    throw new Error(
      `Failed to upload file: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

/**
 * Upload profile avatar image
 *
 * @param file - The image file
 * @param userId - The user ID
 * @returns UploadedFile object
 */
export async function uploadProfileAvatar(
  file: File,
  userId: string,
): Promise<UploadedFile> {
  const fileExtension = file.name.split(".").pop() || "jpg";
  const storagePath = `profiles/${userId}/avatar.${fileExtension}`;
  return uploadFile(file, storagePath);
}

/**
 * Upload verification document
 *
 * @param file - The document file (PDF or image)
 * @param userId - The user ID
 * @param documentType - Type of document ('license' or 'officer_id')
 * @returns UploadedFile object
 */
export async function uploadVerificationDocument(
  file: File,
  userId: string,
  documentType: "license" | "officer_id",
): Promise<UploadedFile> {
  const fileExtension = file.name.split(".").pop() || "pdf";
  const storagePath = `verifications/${userId}/${documentType}.${fileExtension}`;
  return uploadFile(file, storagePath);
}

/**
 * Delete a file from Firebase Storage
 *
 * @param fileUrl - The full URL of the file to delete
 */
export async function deleteFile(fileUrl: string): Promise<void> {
  try {
    const fileRef = ref(storage, fileUrl);
    await deleteObject(fileRef);
    console.log("🗑️ Deleted:", fileRef.fullPath);
  } catch (error) {
    console.error("Failed to delete file:", error);
    // Don't throw - deletion failure shouldn't block other operations
  }
}

/**
 * Delete a listing image from Firebase Storage
 *
 * @param imageUrl - The full URL of the image to delete
 */
export async function deleteListingImage(imageUrl: string): Promise<void> {
  return deleteFile(imageUrl);
}
